import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { FiCheck, FiRefreshCw, FiTrash2, FiPlus, FiX } from 'react-icons/fi';
import { SiClaude, SiDeepseek, SiGooglegemini, SiHubspot, SiWhatsapp, SiWordpress, SiZoho } from 'react-icons/si';
import { RiGrokAiFill, RiOpenaiFill } from 'react-icons/ri';
import { LuLayoutDashboard } from 'react-icons/lu';

const GROUPS = [
  { id: 'ai', title: 'AI assistants & creative tools', note: 'Use your own AI account for writing help, and link Canva for your designs. You pay each provider directly.' },
  { id: 'messaging', title: 'Messaging', note: 'Connect your WhatsApp number or the WhatsApp Business API.' },
  { id: 'business', title: 'Business apps', note: 'Link your website CMS, CRM, HRM or any custom app.' }
];

const FIELD_COPY = {
  secret: { label: 'API key', type: 'password', placeholder: 'Paste the key from your provider' },
  baseUrl: { label: 'App address', type: 'url', placeholder: 'https://your-app.example.com' },
  label: { label: 'Account name or number', type: 'text', placeholder: 'e.g. Sales line +91 98765 43210' }
};

const CHANNEL_COPY = {
  label: { label: 'Channel name', type: 'text', placeholder: 'e.g. Easten Updates' },
  baseUrl: { label: 'Channel link', type: 'url', placeholder: 'https://whatsapp.com/channel/...' }
};

function CanvaMark(props) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false" {...props}>
      <path fill="currentColor" d="M12.4 4.2c-4.1 0-7 3.2-7 7.4 0 3.1 1.9 5.2 4.7 5.2 2.3 0 3.9-1.2 4.9-3.1l-1.5-.9c-.7 1.3-1.6 2.1-3.1 2.1-1.8 0-3-1.4-3-3.4 0-3 1.9-5.1 4.4-5.1 1.4 0 2.3.6 2.9 1.7l1.5-.9c-.9-1.7-2.5-3-4.8-3Z" />
    </svg>
  );
}

// Brand mark and colour for every connectable app, plus one line saying what it is for.
const BRANDS = {
  chatgpt: { Icon: RiOpenaiFill, bg: '#10a37f', blurb: 'Write captions, replies and ideas with your OpenAI account.' },
  claude: { Icon: SiClaude, bg: '#d97757', blurb: 'Long, careful writing and rewrites with your Anthropic account.' },
  gemini: { Icon: SiGooglegemini, bg: '#4285f4', blurb: 'Drafts and translations with your Google AI account.' },
  grok: { Icon: RiGrokAiFill, bg: '#111827', blurb: 'Quick, casual copy with your xAI account.' },
  deepseek: { Icon: SiDeepseek, bg: '#4d6bfe', blurb: 'Low-cost drafts with your DeepSeek account.' },
  canva: { Icon: CanvaMark, bg: 'linear-gradient(135deg, #00c4cc, #7d2ae8)', blurb: 'Sign in to Canva so your team can use its designs with this workspace.' },
  whatsapp: { Icon: SiWhatsapp, bg: '#25d366', blurb: 'Show your WhatsApp number to your team.' },
  whatsapp_api: { Icon: SiWhatsapp, bg: '#128c7e', blurb: 'Business API number for automated chats.' },
  whatsapp_channel: { Icon: SiWhatsapp, bg: '#1fa855', blurb: 'Keep your WhatsApp Channel link with the workspace.' },
  cms: { Icon: SiWordpress, bg: '#21759b', blurb: 'Link your website CMS.' },
  crm: { Icon: SiHubspot, bg: '#ff7a59', blurb: 'Link the CRM where your leads live.' },
  hrm: { Icon: SiZoho, bg: '#e42527', blurb: 'Link your HR system.' },
  custom: { Icon: LuLayoutDashboard, bg: '#2f6fed', blurb: 'Any other app with a web address.' }
};
const FALLBACK_BRAND = { Icon: LuLayoutDashboard, bg: '#2f6fed', blurb: '' };


function fieldsFor(entry) {
  const optional = ['label', 'baseUrl', 'secret'].filter((field) => !entry.needs.includes(field));
  return [...entry.needs, ...optional.filter((field) => field === 'label')];
}

function AppCard({ entry, saved, onConnect, onTest, onRemove, testing }) {
  const status = saved?.lastStatus === 'failed' ? 'failed' : saved?.lastStatus === 'ok' ? 'ok' : saved ? 'saved' : '';
  const brand = BRANDS[entry.provider] || FALLBACK_BRAND;
  const { Icon } = brand;
  return (
    <article className={`settings-app ${saved ? 'connected' : ''}`}>
      <div className="settings-app-top">
        <span className="settings-app-badge" style={{ background: brand.bg }} aria-hidden="true"><Icon /></span>
        <span className={`settings-app-state ${status || 'none'}`}>{status === 'ok' ? 'Working' : status === 'failed' ? 'Key rejected' : saved ? 'Connected' : 'Not connected'}</span>
      </div>
      <div className="settings-app-copy">
        <strong>{entry.name}</strong>
        <small>{saved ? [saved.label, saved.secretHint].filter(Boolean).join(' · ') || brand.blurb : brand.blurb}</small>
        {status === 'ok' && <em className="ok"><FiCheck aria-hidden="true" /> Key accepted</em>}
        {status === 'failed' && <em className="bad">Edit the key and test again.</em>}
      </div>
      <div className="settings-app-actions">
        <button className={saved ? 'btn subtle' : 'btn primary'} type="button" onClick={() => onConnect(entry)}>{saved ? (entry.oauth ? 'Reconnect' : 'Edit') : <><FiPlus aria-hidden="true" />Connect</>}</button>
        {saved && entry.canTest && <button className="btn subtle" type="button" disabled={testing} onClick={() => onTest(entry)}><FiRefreshCw aria-hidden="true" />{testing ? 'Testing' : 'Test'}</button>}
        {saved && <button className="icon-btn" type="button" aria-label={`Disconnect ${entry.name}`} title="Disconnect" onClick={() => onRemove(entry)}><FiTrash2 /></button>}
      </div>
    </article>
  );
}

function ConnectDialog({ entry, saved, onClose, onSave, saving, error }) {
  const [values, setValues] = useState({ label: saved?.label || '', baseUrl: saved?.baseUrl || '', secret: '' });
  const fields = fieldsFor(entry);
  const copy = (field) => (entry.provider === 'whatsapp_channel' && CHANNEL_COPY[field]) || FIELD_COPY[field];
  const submit = (event) => { event.preventDefault(); onSave(entry, values); };
  return (
    <div className="settings-dialog-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <form className="settings-dialog" role="dialog" aria-modal="true" aria-label={`Connect ${entry.name}`} onSubmit={submit}>
        <header><h3>{saved ? 'Edit' : 'Connect'} {entry.name}</h3><button type="button" aria-label="Close" onClick={onClose}><FiX /></button></header>
        {fields.map((field) => <label key={field}>
          <span>{copy(field).label}{!entry.needs.includes(field) && <small> (optional)</small>}</span>
          <input
            autoComplete="off"
            required={entry.needs.includes(field) && !(field === 'secret' && saved)}
            type={copy(field).type}
            placeholder={field === 'secret' && saved ? `Saved (${saved.secretHint}). Leave empty to keep it.` : copy(field).placeholder}
            value={values[field]}
            onChange={(event) => setValues((current) => ({ ...current, [field]: event.target.value }))}
          />
        </label>)}
        <p className="settings-dialog-note">Keys are stored encrypted and are never shown again.</p>
        {error && <p className="settings-dialog-error" role="alert">{error}</p>}
        <footer><button className="btn subtle" type="button" onClick={onClose}>Cancel</button><button className="btn primary" type="submit" disabled={saving}>{saving ? 'Saving' : 'Save'}</button></footer>
      </form>
    </div>
  );
}

function Integrations({ apiBase, workspaceId, setNotice }) {
  const [state, setState] = useState({ loading: true, catalog: [], connected: [], error: '' });
  const [dialog, setDialog] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dialogError, setDialogError] = useState('');
  const [testing, setTesting] = useState('');
  const request = useMemo(() => ({ withCredentials: true, headers: { 'x-workspace-id': workspaceId } }), [workspaceId]);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${apiBase}/api/integrations`, request);
      setState({ loading: false, catalog: data.catalog || [], connected: data.connected || [], error: '' });
    } catch (error) {
      setState({ loading: false, catalog: [], connected: [], error: error.response?.status === 403 ? 'Your account does not have access to manage connected apps.' : 'Connected apps could not be loaded. Try again.' });
    }
  }, [apiBase, request]);
  useEffect(() => { setState((current) => ({ ...current, loading: true })); load(); }, [load]);

  const byProvider = new Map(state.connected.map((item) => [item.provider, item]));
  const save = async (entry, values) => {
    setSaving(true);
    setDialogError('');
    try {
      await axios.put(`${apiBase}/api/integrations/${entry.provider}`, values, request);
      setDialog(null);
      setNotice?.({ type: 'success', message: `${entry.name} connected.` });
      await load();
    } catch (error) {
      setDialogError(error.response?.data?.error || 'Could not save. Try again.');
    } finally {
      setSaving(false);
    }
  };
  const test = async (entry) => {
    setTesting(entry.provider);
    try {
      const { data } = await axios.post(`${apiBase}/api/integrations/${entry.provider}/test`, {}, request);
      setNotice?.({ type: data.ok ? 'success' : 'warning', message: data.message });
    } catch (error) {
      setNotice?.({ type: 'warning', message: error.response?.data?.error || 'The test could not run.' });
    } finally {
      setTesting('');
      load();
    }
  };
  const remove = async (entry) => {
    if (!window.confirm(`Disconnect ${entry.name} from this workspace?`)) return;
    try {
      await axios.delete(`${apiBase}/api/integrations/${entry.provider}`, request);
      setNotice?.({ type: 'success', message: `${entry.name} disconnected.` });
      await load();
    } catch (error) {
      setNotice?.({ type: 'warning', message: error.response?.data?.error || 'Could not disconnect.' });
    }
  };

  if (state.loading) return <p className="settings-muted">Loading connected apps…</p>;
  if (state.error) return <p className="settings-muted" role="alert">{state.error}</p>;
  return (
    <div className="settings-integrations">
      <p className="settings-lead">Connect the tools you already use. Each workspace keeps its own connections.</p>
      {GROUPS.map((group) => {
        const entries = state.catalog.filter((item) => item.group === group.id);
        if (!entries.length) return null;
        return <section className="settings-group" key={group.id}>
          <header><h3>{group.title}</h3><p>{group.note}</p></header>
          <div className="settings-app-grid">
            {entries.map((entry) => <AppCard key={entry.provider} entry={entry} saved={byProvider.get(entry.provider)} testing={testing === entry.provider}
              onConnect={(item) => { if (item.oauth) { window.location.href = `${apiBase}/auth/${item.provider}?workspaceId=${encodeURIComponent(workspaceId)}`; return; } setDialogError(''); setDialog(item); }} onTest={test} onRemove={remove} />)}
          </div>
        </section>;
      })}
      <p className="settings-footnote">Connecting an app saves it securely for this workspace. Features that use these apps are switched on one at a time.</p>
      {dialog && <ConnectDialog entry={dialog} saved={byProvider.get(dialog.provider)} saving={saving} error={dialogError} onClose={() => setDialog(null)} onSave={save} />}
    </div>
  );
}

function Workspaces({ workspaces, workspaceId, onManage }) {
  return (
    <div className="settings-workspaces">
      <p className="settings-lead">Each workspace keeps its own channels, posts, clients and reports.</p>
      <div className="settings-workspace-list">
        {workspaces.map((item) => <article className="settings-workspace" key={item.id}>
          <span className="settings-workspace-avatar" style={{ background: item.color || '#16835f' }} aria-hidden="true">{item.profileImageUrl ? <img src={item.profileImageUrl} alt="" /> : item.initials}</span>
          <div><strong>{item.name}</strong><small>{[item.businessType, item.id === workspaceId ? 'Current workspace' : ''].filter(Boolean).join(' · ') || 'Workspace'}</small></div>
          <button className="btn subtle" type="button" onClick={() => onManage('workspaces')}>Manage</button>
        </article>)}
      </div>
      <button className="btn primary settings-add" type="button" onClick={() => onManage('workspaces')}><FiPlus aria-hidden="true" /> Add workspace</button>
    </div>
  );
}

export default function SettingsPage({ apiBase, workspaceId, workspaces = [], onManageWorkspaces, setNotice }) {
  const [tab, setTab] = useState('workspaces');
  return (
    <section className="settings-page" aria-label="Settings">
      <header className="settings-head">
        <div><h2>Settings</h2><p>Manage your workspaces and the apps connected to them.</p></div>
        <div className="settings-tabs" role="tablist" aria-label="Settings sections">
          <button type="button" role="tab" aria-selected={tab === 'workspaces'} className={tab === 'workspaces' ? 'active' : ''} onClick={() => setTab('workspaces')}>Workspaces</button>
          <button type="button" role="tab" aria-selected={tab === 'integrations'} className={tab === 'integrations' ? 'active' : ''} onClick={() => setTab('integrations')}>Plugins &amp; Integrations</button>
        </div>
      </header>
      {tab === 'workspaces'
        ? <Workspaces workspaces={workspaces} workspaceId={workspaceId} onManage={onManageWorkspaces} />
        : <Integrations apiBase={apiBase} workspaceId={workspaceId} setNotice={setNotice} />}
    </section>
  );
}
